<script setup>
import { ref, useTemplateRef, onMounted, onUnmounted, watch } from 'vue';
import { debounce } from '@chatwoot/utils';

const props = defineProps({
  initialQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['search']);

const searchQuery = ref(props.initialQuery);
const isInputFocused = ref(false);

const searchInput = useTemplateRef('searchInput');

const isEligibleQuery = value => {
  const normalized = String(value || '').trim();
  if (!normalized) return false;
  if (/^\d+$/.test(normalized)) return true;
  return normalized.length >= 3;
};

const handler = e => {
  if (e.key === '/' && document.activeElement.tagName !== 'INPUT') {
    e.preventDefault();
    searchInput.value.focus();
  } else if (e.key === 'Escape' && document.activeElement.tagName === 'INPUT') {
    e.preventDefault();
    searchInput.value.blur();
  }
};

const debouncedEmit = debounce(value => {
  const normalized = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  emit('search', isEligibleQuery(normalized) ? normalized : '');
}, 350);

const onInput = e => {
  searchQuery.value = e.target.value;
  const normalized = searchQuery.value.trim().replace(/\s+/g, ' ');
  if (!normalized) {
    debouncedEmit.cancel?.();
    emit('search', '');
    return;
  }
  debouncedEmit(normalized);
};

const clearInput = () => {
  searchQuery.value = '';
  debouncedEmit.cancel?.();
  emit('search', '');
  searchInput.value?.focus();
};

const onFocus = () => {
  isInputFocused.value = true;
};

const onBlur = () => {
  isInputFocused.value = false;
};

watch(
  () => props.initialQuery,
  value => {
    if (value !== searchQuery.value) searchQuery.value = value;
  }
);

onMounted(() => {
  searchInput.value.focus();
  document.addEventListener('keydown', handler);
});

onUnmounted(() => {
  debouncedEmit.cancel?.();
  document.removeEventListener('keydown', handler);
});
</script>

<template>
  <div class="search-input" :class="{ 'is-focused': isInputFocused }">
    <span
      class="search-input__icon i-lucide-search"
      :class="{ 'is-active': isInputFocused }"
    />
    <input
      ref="searchInput"
      type="search"
      class="search-input__field reset-base no-margin"
      :placeholder="$t('SEARCH.INPUT_PLACEHOLDER')"
      :value="searchQuery"
      @focus="onFocus"
      @blur="onBlur"
      @input="onInput"
    />
    <button
      v-if="searchQuery"
      type="button"
      class="search-input__clear"
      :aria-label="$t('CLEAR')"
      @click="clearInput"
    >
      <span class="i-lucide-x size-4" />
    </button>
    <span class="search-input__kbd">
      {{ $t('SEARCH.PLACEHOLDER_KEYBINDING') }}
    </span>
  </div>
</template>

<style scoped>
.search-input {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid var(--wlab-border-light, var(--n-weak));
  background: var(--wlab-bg-sidebar, var(--n-background));
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.search-input.is-focused {
  border-color: color-mix(
    in srgb,
    var(--wlab-accent, #6366f1) 55%,
    transparent
  );
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--wlab-accent, #6366f1) 18%, transparent);
}

.search-input__icon {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--wlab-text-muted, var(--n-slate-10));
}

.search-input__icon.is-active {
  color: var(--wlab-accent, #6366f1);
}

.search-input__field {
  flex: 1;
  min-width: 0;
  margin: 0;
  border: 0;
  outline: none;
  background: transparent;
  box-shadow: none;
  font-size: 15px;
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-input__field::placeholder {
  color: var(--wlab-text-muted, var(--n-slate-10));
}

.search-input__clear {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--wlab-text-muted, var(--n-slate-10));
  cursor: pointer;
}

.search-input__clear:hover {
  background: color-mix(
    in srgb,
    var(--wlab-text-primary, #fff) 8%,
    transparent
  );
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-input__kbd {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--wlab-border-light, var(--n-weak));
  background: var(--wlab-bg-card, var(--n-solid-3));
  color: var(--wlab-text-muted, var(--n-slate-10));
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
</style>
